import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, BookOpen } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { useAmazonLink } from "@/hooks/useAmazonLink";
import { PROLOGUE } from "@/lib/content";

// Inline paginated prologue reader — 2 paragraphs per page, elegant page turn.
const PER_PAGE = 2;

export default function PrologueReader() {
  const { link: amazonLink } = useAmazonLink();
  const pages = [];
  for (let i = 0; i < PROLOGUE.paragraphs.length; i += PER_PAGE) {
    pages.push(PROLOGUE.paragraphs.slice(i, i + PER_PAGE));
  }
  const total = pages.length;
  const [page, setPage] = useState(0);
  const [direction, setDirection] = useState(1);
  const atEnd = page === total - 1;

  const go = (d) => {
    const next = page + d;
    if (next < 0 || next >= total) return;
    setDirection(d);
    setPage(next);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  return (
    <section
      data-testid="prologue-reader-section"
      className="relative overflow-hidden border-t border-white/10 bg-panel py-24 md:py-32"
    >
      <span className="pointer-events-none absolute -right-6 top-16 select-none font-display text-[22vw] leading-none text-white/[0.03] md:text-[16vw]">
        00
      </span>

      <div className="mx-auto max-w-5xl px-6 md:px-12">
        <div className="mb-10 flex flex-wrap items-end justify-between gap-6 md:mb-14">
          <div>
            <Reveal>
              <p className="mb-4 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.3em] text-rebellion">
                <BookOpen className="h-4 w-4" /> Read the Prologue
              </p>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-display text-4xl uppercase leading-[0.95] tracking-tight text-paper sm:text-5xl md:text-6xl">
                Turn the <span className="text-rebellion">first page.</span>
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <p className="max-w-sm font-editorial text-lg leading-relaxed text-white/60">
              A few paragraphs, right here. No downloads, no ceremony — just
              the opening argument.
            </p>
          </Reveal>
        </div>

        {/* Book pane */}
        <Reveal delay={0.15}>
          <div
            data-testid="prologue-reader-pane"
            className="relative border border-white/10 bg-void shadow-[0_30px_80px_rgba(0,0,0,0.6)]"
          >
            {/* Top ribbon */}
            <div className="flex items-center justify-between gap-4 border-b border-white/10 px-4 py-3 md:px-10">
              <span className="truncate font-mono text-[0.6rem] uppercase tracking-[0.2em] text-white/40 sm:text-[0.65rem] sm:tracking-[0.3em]">
                <span className="hidden sm:inline">Election on Mars — </span>Prologue
              </span>
              <span
                data-testid="prologue-page-indicator"
                className="shrink-0 whitespace-nowrap font-mono text-[0.6rem] uppercase tracking-[0.2em] text-white/40 sm:text-[0.65rem] sm:tracking-[0.3em]"
              >
                Page {String(page + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
              </span>
            </div>

            {/* Page content */}
            <div className="relative min-h-[380px] px-6 py-10 md:min-h-[440px] md:px-16 md:py-14">
              <AnimatePresence mode="wait" custom={direction}>
                <motion.article
                  key={page}
                  custom={direction}
                  initial={{ opacity: 0, x: direction * 40 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -direction * 40 }}
                  transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  className="space-y-6"
                  data-testid={`prologue-page-${page + 1}`}
                >
                  {page === 0 ? (
                    <h3 className="mb-4 font-display text-3xl uppercase tracking-tight text-rebellion md:text-4xl">
                      {PROLOGUE.title}
                    </h3>
                  ) : null}
                  {pages[page].map((p, i) => (
                    <p
                      key={i}
                      className="font-editorial text-lg leading-relaxed text-white/85 md:text-xl"
                    >
                      {p}
                    </p>
                  ))}

                  {atEnd ? (
                    <div className="mt-10 flex flex-wrap items-center gap-4 border-t border-white/10 pt-8">
                      <p className="font-editorial text-lg italic text-white/60">
                        The argument continues in print.
                      </p>
                      <a
                        data-testid="prologue-buy-button"
                        href={amazonLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 border border-rebellion bg-rebellion px-5 py-3 font-mono text-xs font-bold uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-transparent hover:text-rebellion"
                      >
                        Continue on Amazon →
                      </a>
                    </div>
                  ) : null}
                </motion.article>
              </AnimatePresence>
            </div>

            {/* Nav */}
            <div className="flex items-center justify-between border-t border-white/10 px-2 py-3 sm:px-4">
              <button
                data-testid="prologue-prev"
                onClick={() => go(-1)}
                disabled={page === 0}
                className="flex items-center gap-1.5 border border-transparent px-3 py-2 font-mono text-[0.6rem] uppercase tracking-[0.15em] text-paper transition-colors hover:border-white/20 disabled:opacity-30 disabled:hover:border-transparent sm:gap-2 sm:px-4 sm:text-[0.65rem] sm:tracking-[0.2em]"
              >
                <ChevronLeft className="h-4 w-4" /> <span className="hidden xs:inline sm:inline">Prev</span>
              </button>
              <div className="flex items-center gap-2">
                {pages.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      setDirection(i > page ? 1 : -1);
                      setPage(i);
                    }}
                    className={`h-[3px] w-5 transition-colors sm:w-6 ${
                      i === page ? "bg-rebellion" : "bg-white/15 hover:bg-white/30"
                    }`}
                    data-testid={`prologue-dot-${i + 1}`}
                    aria-label={`Go to page ${i + 1}`}
                  />
                ))}
              </div>
              <button
                data-testid="prologue-next"
                onClick={() => go(1)}
                disabled={atEnd}
                className="flex items-center gap-1.5 border border-transparent px-3 py-2 font-mono text-[0.6rem] uppercase tracking-[0.15em] text-paper transition-colors hover:border-white/20 disabled:opacity-30 disabled:hover:border-transparent sm:gap-2 sm:px-4 sm:text-[0.65rem] sm:tracking-[0.2em]"
              >
                <span className="hidden xs:inline sm:inline">Next</span> <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
